import { useState, useEffect, useRef, useCallback } from "react";
import AdminLogin from "./AdminLogin";
import Studio from "./Studio";
import { isConfigured, cardPaymentsAllowed } from "./lib/supabase";
import * as api from "./lib/api";
import { LOGO, SEED_PAINTINGS, SEED_SETTINGS, SEED_SHOP } from "./data";
import { pathFor, parseLocation } from "./route";
import { Ic, P, Toast, CurrencyPicker } from "./ui";
import { Link, Home, Gallery, Prints, ShopItemPage, Detail, About, Contact, Wishlist, CartPage, Checkout, Confirm } from "./pages";
import { cartLines, cartTotal, orderItems, shopToken } from "./lib/cart";
import { CurrencyProvider } from "./lib/currency";
import "./styles.css";

/* ============================================================
   Karima El Ghobri — Original Oil Paintings
   App shell: header, footer, routing and all the data wiring.
   Look & feel lives in src/styles.css, pages in src/pages.jsx,
   the artist's admin in src/Studio.jsx, data access in src/lib/api.js.
   Cart, wishlist and theme stay in this browser.
   ============================================================ */

const K = { wishlist: "kg:wishlist", theme: "kg:theme" };
const store = {
  get(key) {
    try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* blocked or full */ }
  },
};

const NAV = [["home", "Home"], ["gallery", "Gallery"], ["prints", "Prints"], ["about", "About"], ["contact", "Contact"]];

export default function App() {
  const [theme, setTheme] = useState(() => {
    const saved = store.get(K.theme);
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const [route, setRoute] = useState(() => ({ ...parseLocation(), cat: "all" }));
  const [paintings, setPaintingsState] = useState(isConfigured ? [] : SEED_PAINTINGS);
  const [shopItems, setShopItemsState] = useState(isConfigured ? [] : SEED_SHOP);
  const [orders, setOrdersState] = useState([]);
  const [messages, setMessagesState] = useState([]);
  const [testimonials, setTestimonialsState] = useState([]);
  const [news, setNewsState] = useState([]);
  const [settings, setSettingsState] = useState(SEED_SETTINGS);
  const [wishlist, setWishlistState] = useState(() => store.get(K.wishlist) || []);
  const [cart, setCart] = useState([]);
  const [lastOrder, setLastOrder] = useState(null);
  const [toast, setToast] = useState("");
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(!isConfigured); /* local preview: studio is open */
  const [authChecked, setAuthChecked] = useState(!isConfigured);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioLoaded, setStudioLoaded] = useState(false);
  const [cardEnabled, setCardEnabled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toastTimer = useRef(null);

  const notify = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2600);
  };

  /* ---- theme: colour scheme on <html> so the page behind everything matches ---- */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0d1114" : "#faf8f4");
  }, [theme]);

  /* ---- header gets a hairline once the page scrolls ---- */
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  /* ---- what every visitor loads ---- */
  const refreshPublic = useCallback(async () => {
    const { paintings: pt, shopItems: sh, testimonials: ts, settings: st } =
      await api.loadPublic(isConfigured ? [] : SEED_PAINTINGS, SEED_SETTINGS);
    setPaintingsState(pt);
    setShopItemsState(sh || []);
    setTestimonialsState(ts);
    setSettingsState(st);
  }, []);

  useEffect(() => {
    (async () => {
      /* Coming back from Stripe's payment page. The money is confirmed by the
         webhook, not by this URL — all we do here is show the right screen. */
      const q = new URLSearchParams(window.location.search);
      const outcome = q.get("checkout");
      if (outcome === "success") {
        setLastOrder({ ref: q.get("ref") || "", mode: "card", items: [] });
        setCart([]);
        setRoute({ page: "confirm", id: null, cat: "all" });
        /* Have the server check with Stripe and finish the order, in case the
           webhook hasn't arrived. Then reload the gallery so the painting
           shows as sold straight away. */
        const sessionId = q.get("session_id");
        if (sessionId) {
          api.confirmCardPayment(sessionId).then((r) => {
            if (r.status === "paid") refreshPublic();
          });
        }
      } else if (outcome === "cancelled") {
        notify("Payment cancelled — your cart is still here");
      }
      if (outcome) window.history.replaceState(null, "", "/");

      try { await refreshPublic(); } finally { setReady(true); }
    })();
  }, [refreshPublic]);

  /* Browser Back / Forward, and typing #studio onto an already-open page. */
  useEffect(() => {
    const onNav = () => { setRoute({ ...parseLocation(), cat: "all" }); setMenuOpen(false); window.scrollTo({ top: 0 }); };
    window.addEventListener("popstate", onNav);
    window.addEventListener("hashchange", onNav);
    return () => { window.removeEventListener("popstate", onNav); window.removeEventListener("hashchange", onNav); };
  }, []);

  /* ---- can the server take card payments right now? ---- */
  useEffect(() => {
    if (!cardPaymentsAllowed) return undefined;
    let alive = true;
    api.checkCardPayments().then((ok) => { if (alive) setCardEnabled(ok); });
    return () => { alive = false; };
  }, []);

  /* ---- is the person at the keyboard the artist? ---- */
  useEffect(() => {
    if (!isConfigured) return undefined;
    let alive = true;
    const apply = async (sess) => {
      const admin = await api.checkIsAdmin(sess).catch(() => false);
      if (!alive) return;
      setSession(sess);
      setIsAdmin(admin);
      setAuthChecked(true);
      if (!admin) { setStudioLoaded(false); setOrdersState([]); setMessagesState([]); setNewsState([]); }
    };
    api.getSession().then(apply);
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!isConfigured) return undefined;
    return api.onAuthChange(async (sess) => {
      const admin = await api.checkIsAdmin(sess);
      setSession(sess);
      setIsAdmin(admin);
      setAuthChecked(true);
      if (!admin) { setStudioLoaded(false); setOrdersState([]); setMessagesState([]); setNewsState([]); }
    });
  }, []);

  /* ---- orders, messages and subscribers: only ever fetched for the artist ---- */
  const refreshStudio = useCallback(async () => {
    setStudioLoading(true);
    try {
      const { orders: o, messages: m, news: n } = await api.loadStudio();
      setOrdersState(o); setMessagesState(m); setNewsState(n);
    } finally {
      setStudioLoading(false); setStudioLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (route.page === "studio" && isAdmin && !studioLoaded) refreshStudio();
  }, [route.page, isAdmin, studioLoaded, refreshStudio]);

  const setWishlist = (v) => { setWishlistState(v); store.set(K.wishlist, v); };

  const go = (page, id = null, extra = {}) => {
    const url = pathFor(page, id);
    if (window.location.pathname + window.location.hash !== url) window.history.pushState(null, "", url);
    setRoute({ page, id, cat: extra.cat || "all" });
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  /* A "confirmation" address only makes sense right after an order. */
  const page = route.page === "confirm" && !lastOrder ? "home" : route.page;

  useEffect(() => {
    const names = { home: "Karima El Ghobri — Original Oil Paintings", gallery: "Gallery — Karima El Ghobri",
      prints: "Prints & small things — Karima El Ghobri",
      about: "About — Karima El Ghobri", contact: "Contact — Karima El Ghobri", cart: "Cart — Karima El Ghobri",
      checkout: "Checkout — Karima El Ghobri", confirm: "Thank you — Karima El Ghobri",
      wishlist: "Wishlist — Karima El Ghobri", studio: "Studio — Karima El Ghobri" };
    const art = page === "painting" ? paintings.find((p) => p.id === route.id) : null;
    document.title = art ? `${art.title} — Karima El Ghobri` : names[page] || names.home;
  }, [page, route.id, paintings]);

  const toggleTheme = () => { const t = theme === "light" ? "dark" : "light"; setTheme(t); store.set(K.theme, t); };
  const toggleWish = (id) => {
    const next = wishlist.includes(id) ? wishlist.filter((x) => x !== id) : [...wishlist, id];
    setWishlist(next);
    notify(wishlist.includes(id) ? "Removed from wishlist" : "Saved to your wishlist");
  };
  /* An original can be in the cart once; there is only one of it. */
  const addCart = (id) => { if (!cart.includes(id)) { setCart([...cart, id]); notify("Added to cart — originals are one of a kind"); } };

  /* A print, sticker or bookmark can be added again and again — the cart holds
     one token per copy, and the cart page counts the repeats. */
  const addShop = (item, variant) => {
    setCart((c) => [...c, shopToken(item.id, variant.id)]);
    notify(`Added — ${item.title} (${variant.label})`);
  };

  /* Removes one copy, or the whole line when `all` is set. Paintings only ever
     have one copy, so both do the same thing for them. */
  const removeLine = (token, all = false) => setCart((c) => {
    if (all) return c.filter((x) => x !== token);
    const at = c.indexOf(token);
    if (at === -1) return c;
    return [...c.slice(0, at), ...c.slice(at + 1)];
  });
  const addLine = (token) => setCart((c) => [...c, token]);
  const subscribe = async (email) => {
    const { already, error } = await api.subscribeEmail(email);
    if (error) { notify("That didn't go through — please try again"); return; }
    if (already) { notify("You're already on the studio letter"); return; }
    notify("Welcome to the studio letter ♡");
    setStudioLoaded(false);
  };

  const sendMessage = async (f) => {
    const { error } = await api.createMessage(f);
    if (error) { notify(error.message || "Your message didn't send — please email me directly"); return; }
    notify("Message delivered to the studio");
    setStudioLoaded(false);
  };

  /* Card payment: hand the cart to our own server function, which prices it
     from the database and returns a Stripe page to send the buyer to. */
  const payByCard = async (f) => {
    const { url, error, soldIds } = await api.startCardCheckout({
      items: cart, name: f.name, email: f.email, note: f.note,
    });
    if (url) { window.location.assign(url); return; }
    notify(error);
    /* If something sold while they were deciding, take it out of the cart and
       show the gallery as it now stands. */
    if (soldIds && soldIds.length > 0) {
      setCart(cart.filter((token) => !soldIds.includes(token)));
      refreshPublic();
    }
  };

  const placeOrder = async (o) => {
    const { data, error } = await api.createOrder(o);
    if (error) { notify("The order didn't go through — please try again"); return; }
    setLastOrder(data);
    setCart([]);
    setStudioLoaded(false);
    go("confirm");
  };

  /* ---- artist actions. Every one of these also has to pass the database
         policies in supabase/schema.sql, so a visitor poking at the code
         still gets nowhere. ---- */
  const savePaintingOp = async (art) => {
    const { data, error } = await api.savePainting(art);
    if (error) { notify("Couldn't save — " + (error.message || "please try again")); return false; }
    const exists = paintings.some((x) => x.id === (data && data.id));
    setPaintingsState(exists ? paintings.map((x) => (x.id === data.id ? data : x)) : [data, ...paintings]);
    notify(art.id ? "Painting updated" : "Painting added to the gallery");
    return true;
  };

  const patchPaintingOp = async (id, patch) => {
    const before = paintings;
    setPaintingsState(paintings.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const { error } = await api.patchPainting(id, patch);
    if (error) { setPaintingsState(before); notify("Couldn't save that change"); }
  };

  const reorderPaintingsOp = async (list) => {
    const before = paintings;
    setPaintingsState(list);
    const { error } = await api.reorderPaintings(list.map((x) => x.id));
    if (error) { setPaintingsState(before); notify("Couldn't save the new order — run supabase/007 first if you haven't"); }
    else notify("Gallery order saved");
  };

  const removePaintingOp = async (id) => {
    const { error } = await api.deletePainting(id);
    if (error) { notify("Couldn't remove that painting"); return; }
    setPaintingsState(paintings.filter((x) => x.id !== id));
    notify("Painting removed");
  };

  const patchOrderOp = async (ref, patch) => {
    const before = orders;
    setOrdersState(orders.map((x) => (x.ref === ref ? { ...x, ...patch } : x)));
    const { error } = await api.patchOrder(ref, patch);
    if (error) { setOrdersState(before); notify("Couldn't update that order"); }
  };

  const addTestimonialOp = async (name, text) => {
    const { data, error } = await api.createTestimonial(name, text);
    if (error) { notify("Couldn't add that testimonial"); return false; }
    setTestimonialsState([...testimonials, data]);
    notify("Testimonial added");
    return true;
  };

  const removeTestimonialOp = async (id) => {
    const { error } = await api.deleteTestimonial(id);
    if (error) { notify("Couldn't remove that testimonial"); return; }
    setTestimonialsState(testimonials.filter((x) => x.id !== id));
  };

  /* ---- prints, stickers & bookmarks ---- */
  const saveShopItemOp = async (item) => {
    const { data, error } = await api.saveShopItem(item);
    if (error) { notify("Couldn't save — " + (error.message || "please try again")); return false; }
    const exists = shopItems.some((x) => x.id === (data && data.id));
    setShopItemsState(exists ? shopItems.map((x) => (x.id === data.id ? data : x)) : [data, ...shopItems]);
    notify(item.id ? "Item updated" : "Item added to the shop");
    return true;
  };

  const patchShopItemOp = async (id, patch) => {
    const before = shopItems;
    setShopItemsState(shopItems.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const { error } = await api.patchShopItem(id, patch);
    /* Put the old value back rather than leaving the Studio showing a change
       the database never accepted. */
    if (error) { setShopItemsState(before); notify("Couldn't save that change"); }
  };

  const removeShopItemOp = async (id) => {
    const { error } = await api.deleteShopItem(id);
    if (error) { notify("Couldn't remove that item"); return; }
    setShopItemsState(shopItems.filter((x) => x.id !== id));
    notify("Item removed from the shop");
  };

  const saveSettingsOp = async (next) => {
    const { error } = await api.saveSettings(next);
    if (error) { notify("Couldn't save settings"); return; }
    setSettingsState(next);
    notify("Settings saved");
  };

  const handleSignOut = async () => {
    await api.signOut();
    setSession(null); setIsAdmin(false); setStudioLoaded(false);
    setOrdersState([]); setMessagesState([]); setNewsState([]);
    go("home");
  };

  const art = page === "painting" ? paintings.find((p) => p.id === route.id) : null;
  const shopItem = page === "shopitem" ? shopItems.find((x) => x.id === route.id) : null;
  const logo = theme === "light" ? LOGO.light : LOGO.dark;
  /* Worked out in one place so the cart, the checkout and the order record can
     never disagree about what is being bought or what it costs. */
  const lines = cartLines(cart, paintings, shopItems);
  const total = cartTotal(lines);

  return (
    <CurrencyProvider loadRates={api.loadRates}>
    <div className="kg-root">
      <a className="kg-skip" href="#main">Skip to content</a>

      <header className={"kg-header" + (scrolled || menuOpen ? " scrolled" : "")}>
        <div className="kg-wrap kg-header-in">
          <Link page="home" go={go} className="kg-logolink" aria-label="Karima El Ghobri — home">
            <img src={logo} alt="Karima El Ghobri" className="kg-logo" />
          </Link>
          <nav className="kg-nav" aria-label="Main">
            {NAV.map(([v, t]) => (
              <Link key={v} page={v} go={go} className={"kg-navlink" + (page === v ? " on" : "")}
                aria-current={page === v ? "page" : undefined}>{t}</Link>
            ))}
          </nav>
          <div className="kg-actions">
            <CurrencyPicker />
            <button className="kg-iconbtn" onClick={toggleTheme} aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}>
              <Ic d={theme === "light" ? P.moon : P.sun} size={19} />
            </button>
            <Link page="wishlist" go={go} className="kg-iconbtn" aria-label={`Wishlist, ${wishlist.length} saved`}>
              <Ic d={P.heart} size={19} />{wishlist.length > 0 && <span className="kg-badge">{wishlist.length}</span>}
            </Link>
            <Link page="cart" go={go} className="kg-iconbtn" aria-label={`Cart, ${cart.length} ${cart.length === 1 ? "item" : "items"}`}>
              <Ic d={P.bag} size={19} />{cart.length > 0 && <span className="kg-badge">{cart.length}</span>}
            </Link>
            <button className="kg-iconbtn kg-menubtn" onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
              <Ic d={menuOpen ? P.x : P.menu} size={21} />
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="kg-mobilenav" aria-label="Mobile">
            {NAV.map(([v, t]) => (
              <Link key={v} page={v} go={go} className={page === v ? "on" : ""}>{t}</Link>
            ))}
            <Link page="wishlist" go={go}>Wishlist</Link>
            <CurrencyPicker />
          </nav>
        )}
      </header>

      {!ready ? (
        <div className="kg-loading">
          <img src={logo} alt="" />
          <div className="kg-caps">Hanging the paintings…</div>
        </div>
      ) : (
        <main id="main">
          {page === "home" && <Home paintings={paintings} testimonials={testimonials} settings={settings} go={go} wishlist={wishlist} toggleWish={toggleWish} subscribe={subscribe} />}
          {page === "gallery" && <Gallery paintings={paintings} go={go} wishlist={wishlist} toggleWish={toggleWish} initialCat={route.cat || "all"} />}
          {page === "prints" && <Prints shopItems={shopItems} go={go} initialKind={route.kind} />}
          {page === "shopitem" && <ShopItemPage item={shopItem} shopItems={shopItems} go={go} cart={cart} addShop={addShop} notify={notify} />}
          {page === "painting" && <Detail art={art} paintings={paintings} go={go} cart={cart} addCart={addCart} wishlist={wishlist} toggleWish={toggleWish} notify={notify} />}
          {page === "about" && <About paintings={paintings} settings={settings} go={go} />}
          {page === "contact" && <Contact settings={settings} sendMessage={sendMessage} />}
          {page === "wishlist" && <Wishlist paintings={paintings} wishlist={wishlist} go={go} toggleWish={toggleWish} />}
          {page === "cart" && <CartPage lines={lines} total={total} removeLine={removeLine} addLine={addLine} go={go} />}
          {page === "checkout" && <Checkout lines={lines} total={total} orderLines={orderItems(lines)} placeOrder={placeOrder}
            payByCard={payByCard} cardEnabled={cardEnabled} go={go} />}
          {page === "confirm" && <Confirm order={lastOrder} go={go} />}
          {page === "studio" && (
            !authChecked
              ? <div className="kg-page kg-wrap kg-section"><p className="kg-muted">Checking…</p></div>
              : isAdmin
                ? <Studio paintings={paintings} shopItems={shopItems}
                    saveShopItem={saveShopItemOp} patchShopItem={patchShopItemOp} removeShopItem={removeShopItemOp}
                    savePainting={savePaintingOp} patchPainting={patchPaintingOp} reorderPaintings={reorderPaintingsOp}
                    removePainting={removePaintingOp} orders={orders} patchOrder={patchOrderOp}
                    messages={messages} testimonials={testimonials} addTestimonial={addTestimonialOp}
                    removeTestimonial={removeTestimonialOp} news={news} settings={settings}
                    saveSettings={saveSettingsOp} notify={notify} onSignOut={handleSignOut}
                    studioLoading={studioLoading} />
                : <AdminLogin notAdmin={Boolean(session)} onSignOut={handleSignOut}
                    onDone={() => { if (!isConfigured) setIsAdmin(true); }} />
          )}
        </main>
      )}

      <footer className="kg-footer">
        <div className="kg-wrap">
          <div className="kg-footer-grid">
            <div>
              <img src={logo} alt="" className="kg-footer-logo" />
              <p className="kg-muted kg-footer-blurb">Original oil paintings from the studio of Karima El Ghobri.
                Each work is one of a kind — painted, signed, and sent with care.</p>
              <div className="kg-social">
                <a className="kg-iconbtn" href={settings.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Ic d={P.ig} size={19} /></a>
                <a className="kg-iconbtn" href={settings.youtube} target="_blank" rel="noreferrer" aria-label="YouTube"><Ic d={P.yt} size={19} /></a>
                <a className="kg-iconbtn" href={"mailto:" + settings.email} aria-label="Email"><Ic d={P.mail} size={19} /></a>
              </div>
            </div>
            <div>
              <div className="kg-caps">Visit</div>
              <ul className="kg-footlinks">
                {NAV.map(([v, t]) => <li key={v}><Link page={v} go={go}>{t}</Link></li>)}
                <li><Link page="wishlist" go={go}>Wishlist</Link></li>
              </ul>
            </div>
            <div>
              <div className="kg-caps">Studio</div>
              <ul className="kg-footlinks">
                <li><a href={"mailto:" + settings.email}>{settings.email}</a></li>
                <li><Link page="contact" go={go}>Commissions</Link></li>
                {isAdmin && <li><Link page="studio" go={go}>Artist studio</Link></li>}
              </ul>
            </div>
          </div>
          <p className="kg-small kg-muted kg-legal">© {new Date().getFullYear()} Karima El Ghobri. All artwork and images are the property of the artist.</p>
        </div>
      </footer>
      <Toast msg={toast} />
    </div>
    </CurrencyProvider>
  );
}
